import { useState, useEffect } from 'react';
import { Calculator, MapPin, DollarSign, TrendingDown, TrendingUp, AlertCircle } from 'lucide-react';
import AdSenseCard from '../components/AdSenseCard';
import { ADSENSE_CONFIG } from '../config/adsense.config';
import '../styles/CalculatorPage.css';

const CalculatorPage = ({ translations, language }) => {
  const t = translations[language];

  // Province tax rates (GST + PST/HST combined) - All 13 provinces and territories
  const TAX_RATES = {
    AB: 0.05,      // 5% GST
    BC: 0.12,      // 5% GST + 7% PST
    MB: 0.12,      // 5% GST + 7% RST
    NB: 0.15,      // 15% HST
    NL: 0.15,      // 15% HST
    NS: 0.15,      // 15% HST
    ON: 0.13,      // 13% HST
    PE: 0.15,      // 15% HST
    QC: 0.14975,   // 5% GST + 9.975% QST
    SK: 0.11,      // 5% GST + 6% PST
    NT: 0.05,      // 5% GST
    NU: 0.05,      // 5% GST
    YT: 0.05       // 5% GST
  };

  const PROVINCE_NAMES = {
    AB: 'Alberta',
    BC: 'British Columbia',
    MB: 'Manitoba',
    NB: 'New Brunswick',
    NL: 'Newfoundland and Labrador',
    NS: 'Nova Scotia',
    ON: 'Ontario',
    PE: 'Prince Edward Island',
    QC: 'Quebec',
    SK: 'Saskatchewan',
    NT: 'Northwest Territories',
    NU: 'Nunavut',
    YT: 'Yukon'
  };

  const [selectedProvince, setSelectedProvince] = useState('');
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [formData, setFormData] = useState({
    currentPlanBeforeTax: '',
    currentPlanAfterTax: '',
    phonePrice: '',
    devicePlanPrice: '',
    byodPlanPrice: '',
    giftCard: '',
    bringItBack: '',
    additionalDiscount: '',
    monthlyPlanDiscount: '',
    msrp: ''
  });
  const [result, setResult] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState('');

  // Detect user's province based on geolocation
  useEffect(() => {
    const detectProvince = async () => {
      setDetectingLocation(true);

      if ('geolocation' in navigator) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 5000,
              enableHighAccuracy: false
            });
          });

          const { latitude, longitude } = position.coords;
          const province = getProvinceFromCoordinates(latitude, longitude);
          if (province) {
            setSelectedProvince(province);
          }
        } catch (error) {
          console.log('Location detection failed:', error);
          setSelectedProvince('ON');
        }
      } else {
        setSelectedProvince('ON');
      }

      setDetectingLocation(false);
    };

    detectProvince();
  }, []);

  // Simple province detection based on coordinates
  const getProvinceFromCoordinates = (lat, lng) => {
    if (lng < -57 && lng > -80 && lat > 45 && lat < 62) return 'QC';
    if (lng < -74 && lng > -95 && lat > 41.5 && lat < 57) return 'ON';
    if (lng < -114 && lng > -139 && lat > 48 && lat < 60) return 'BC';
    if (lng < -110 && lng > -120 && lat > 49 && lat < 60) return 'AB';
    if (lng < -95 && lng > -102 && lat > 49 && lat < 60) return 'MB';
    if (lng < -102 && lng > -110 && lat > 49 && lat < 60) return 'SK';
    if (lng < -59.7 && lng > -66.5 && lat > 43 && lat < 47) return 'NS';
    if (lng < -63.7 && lng > -69.1 && lat > 44.5 && lat < 48.1) return 'NB';
    if (lng < -52.6 && lng > -67.8 && lat > 46.6 && lat < 60.4) return 'NL';
    if (lng < -61.9 && lng > -64.4 && lat > 45.9 && lat < 47.1) return 'PE';
    return 'ON';
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    const numValue = value === '' ? '' : parseFloat(value);

    // Bidirectional tax calculation for current plan fields
    if (field === 'currentPlanBeforeTax') {
      if (value !== '') {
        const taxRate = TAX_RATES[selectedProvince] || 0.13;
        const afterTax = (numValue * (1 + taxRate)).toFixed(2);
        setFormData({
          ...formData,
          currentPlanBeforeTax: value,
          currentPlanAfterTax: afterTax
        });
      } else {
        // Clear both fields when before tax is emptied
        setFormData({
          ...formData,
          currentPlanBeforeTax: '',
          currentPlanAfterTax: ''
        });
      }
    } else if (field === 'currentPlanAfterTax') {
      if (value !== '') {
        const taxRate = TAX_RATES[selectedProvince] || 0.13;
        const beforeTax = (numValue / (1 + taxRate)).toFixed(2);
        setFormData({
          ...formData,
          currentPlanBeforeTax: beforeTax,
          currentPlanAfterTax: value
        });
      } else {
        // Clear both fields when after tax is emptied
        setFormData({
          ...formData,
          currentPlanBeforeTax: '',
          currentPlanAfterTax: ''
        });
      }
    } else {
      setFormData({
        ...formData,
        [field]: value
      });
    }
  };

  // Calculate results
  const calculateDeal = () => {
    if (!formData.phonePrice || !formData.devicePlanPrice || !formData.byodPlanPrice) {
      alert(t.requiredFieldsAlert);
      return;
    }

    const taxRate = TAX_RATES[selectedProvince] || 0.13;
    const contractLength = 24; // 2 years in months

    // Parse values and round to 2 decimal places to avoid floating-point precision issues
    const phoneMonthlyPrice = Math.round(parseFloat(formData.phonePrice) * 100) / 100;
    const devicePlanPrice = Math.round(parseFloat(formData.devicePlanPrice) * 100) / 100;
    const byodPlanPrice = Math.round(parseFloat(formData.byodPlanPrice) * 100) / 100;
    const giftCard = formData.giftCard ? Math.round(parseFloat(formData.giftCard) * 100) / 100 : 0;
    const bringItBack = formData.bringItBack ? Math.round(parseFloat(formData.bringItBack) * 100) / 100 : 0;
    const additionalDiscount = formData.additionalDiscount ? Math.round(parseFloat(formData.additionalDiscount) * 100) / 100 : 0;
    const monthlyPlanDiscount = formData.monthlyPlanDiscount ? Math.round(parseFloat(formData.monthlyPlanDiscount) * 100) / 100 : 0;
    const msrp = formData.msrp ? Math.round(parseFloat(formData.msrp) * 100) / 100 : null;

    // Apply monthly plan discount to both plans
    const adjustedDevicePlanPrice = devicePlanPrice - monthlyPlanDiscount;
    const adjustedByodPlanPrice = byodPlanPrice - monthlyPlanDiscount;

    // Calculate differences
    const planDifference = adjustedDevicePlanPrice - adjustedByodPlanPrice;
    const planDifferenceMonthly = planDifference;
    const planDifferenceTotal = planDifference * contractLength;

    // Phone costs
    const phoneUpfrontCost = phoneMonthlyPrice * contractLength;
    const realPhoneCost = phoneUpfrontCost + planDifferenceTotal;

    // Gift card pre-tax value (round to avoid floating-point precision issues)
    const giftCardPreTaxValue = giftCard > 0 ? Math.round((giftCard / (1 + taxRate)) * 100) / 100 : 0;

    // Calculate effective monthly device cost
    // Additional discount is amortized over 24 months
    const additionalDiscountMonthly = additionalDiscount / contractLength;
    const realDeviceMonthlyPrice = phoneMonthlyPrice + planDifferenceMonthly - additionalDiscountMonthly;

    // Bring-It-Back Premium increases the effective phone cost (amount you pay to keep the phone)
    // Additional Discount reduces the effective phone cost (loyalty credits, winback offers)
    // Use pre-tax gift card value for accurate calculation
    const effectivePhoneCost = realPhoneCost - giftCardPreTaxValue + bringItBack - additionalDiscount;

    // Calculate effective device cost including bring-it-back (for monthly display)
    const effectiveDeviceCostWithBringItBack = effectivePhoneCost;
    const effectiveDeviceMonthlyPrice = effectiveDeviceCostWithBringItBack / contractLength;

    // Plan costs
    const totalPlanCost = adjustedDevicePlanPrice * contractLength;
    const extraPlanSpending = planDifferenceTotal;

    // Effective device financing cost (device payment minus additional discount)
    const effectiveDeviceFinancing = phoneUpfrontCost - additionalDiscount;

    // Total cost (plan + effective device financing)
    const totalCost = totalPlanCost + effectiveDeviceFinancing;

    // Monthly cost of new plan + device
    const totalMonthlyCost = totalCost / contractLength;

    // Current plan comparison
    const currentPlanAfterTax = formData.currentPlanAfterTax ? Math.round(parseFloat(formData.currentPlanAfterTax) * 100) / 100 : 0;
    const newPlanMonthlyPrice = adjustedDevicePlanPrice;
    const planComparison = currentPlanAfterTax > 0 ? currentPlanAfterTax - newPlanMonthlyPrice : null;
    const totalMonthlyComparison = currentPlanAfterTax > 0 ? currentPlanAfterTax - totalMonthlyCost : null;

    // Savings (only if MSRP is provided)
    let theoreticalSavings = null;
    let savingsPercentage = null;
    let dealQuality = null; // 'excellent', 'good', 'fair', 'bad'

    if (msrp) {
      theoreticalSavings = msrp - effectivePhoneCost;
      savingsPercentage = ((theoreticalSavings / msrp) * 100).toFixed(1);

      // Determine deal quality based on savings percentage
      if (savingsPercentage >= 60) {
        dealQuality = 'excellent';
      } else if (savingsPercentage >= 15) {
        dealQuality = 'good';
      } else if (savingsPercentage >= 5 && theoreticalSavings >= 0) {
        dealQuality = 'fair';
      } else {
        dealQuality = 'bad';
      }
    }

    setResult({
      phoneMonthlyPrice,
      planDifferenceMonthly,
      planDifferenceTotal,
      phoneUpfrontCost,
      realPhoneCost,
      realDeviceMonthlyPrice,
      effectiveDeviceMonthlyPrice,
      additionalDiscountMonthly,
      giftCard,
      giftCardPreTaxValue,
      bringItBack,
      additionalDiscount,
      monthlyPlanDiscount,
      effectivePhoneCost,
      devicePlanPrice,
      adjustedDevicePlanPrice,
      totalPlanCost,
      totalCost,
      totalMonthlyCost,
      effectiveDeviceFinancing,
      msrp,
      theoreticalSavings,
      savingsPercentage,
      extraPlanSpending,
      dealQuality,
      contractLength,
      hasMSRP: msrp !== null,
      currentPlanAfterTax,
      planComparison,
      totalMonthlyComparison
    });
  };

  const formatCurrency = (value) => {
    return `$${value.toFixed(2)}`;
  };

  // Helper function to convert markdown-style bold (**text**) to React elements
  const renderBoldText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <div className="calculator-page">
      <div className="calculator-container">

        {/* Input Form */}
        <div className="calculator-form">
          <div className="calculator-header">
            <Calculator size={32} />
            <h1>{t.calculatorTitle}</h1>
            <p className="subtitle">{t.calculatorSubtitle}</p>
          </div>
          {/* Device & Plan Details - Required Fields with Optional MSRP */}
          <div className="form-section device-plan-section">
            <h3>{t.devicePlanDetails}</h3>
            <div className="input-fields-container">
              <div className="input-group">
                <label className="label-with-hover-tooltip" onMouseEnter={() => setTooltipVisible('phonePrice')} onMouseLeave={() => setTooltipVisible('')}>
                  {t.devicePrice} *
                  {tooltipVisible === 'phonePrice' && <div className="tooltip-content-hover">{t.devicePriceTooltip}</div>}
                </label>
                <input type="number" step="0.01" value={formData.phonePrice} onChange={(e) => handleInputChange('phonePrice', e.target.value)} placeholder={t.mandatoryField} required />
              </div>

              <div className="input-group">
                <label className="label-with-hover-tooltip" onMouseEnter={() => setTooltipVisible('devicePlan')} onMouseLeave={() => setTooltipVisible('')}>
                  {t.devicePlanPrice} *
                  {tooltipVisible === 'devicePlan' && <div className="tooltip-content-hover">{t.devicePlanPriceTooltip}</div>}
                </label>
                <input type="number" step="0.01" value={formData.devicePlanPrice} onChange={(e) => handleInputChange('devicePlanPrice', e.target.value)} placeholder={t.mandatoryField} required />
              </div>

              <div className="input-group">
                <label className="label-with-hover-tooltip" onMouseEnter={() => setTooltipVisible('byodPlan')} onMouseLeave={() => setTooltipVisible('')}>
                  {t.byodPlanPrice} *
                  {tooltipVisible === 'byodPlan' && <div className="tooltip-content-hover">{t.byodPlanPriceTooltip}</div>}
                </label>
                <input type="number" step="0.01" value={formData.byodPlanPrice} onChange={(e) => handleInputChange('byodPlanPrice', e.target.value)} placeholder={t.mandatoryField} required />
              </div>

              <div className="input-group input-group-optional">
                <label className="label-with-hover-tooltip" onMouseEnter={() => setTooltipVisible('msrp')} onMouseLeave={() => setTooltipVisible('')}>
                  {t.msrpDevice}
                  {tooltipVisible === 'msrp' && <div className="tooltip-content-hover">{t.msrpDeviceTooltip}</div>}
                </label>
                <input type="number" step="0.01" value={formData.msrp} onChange={(e) => handleInputChange('msrp', e.target.value)} placeholder={t.optionalFieldPlaceholder} />
              </div>
            </div>
          </div>

          {/* Current Plan - Optional */}
          <div className="form-section optional-section">
            <h3>{t.currentPlanOptional}</h3>
            <p className="helper-text">
              {t.currentPlanHelper}
            </p>
            <div className="input-row">
              <div className="input-group input-group-optional">
                <label>{t.beforeTax}</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.currentPlanBeforeTax}
                  onChange={(e) => handleInputChange('currentPlanBeforeTax', e.target.value)}
                  placeholder={t.optionalFieldPlaceholder}
                />
              </div>
              <div className="input-group input-group-optional">
                <label>{t.afterTax}</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.currentPlanAfterTax}
                  onChange={(e) => handleInputChange('currentPlanAfterTax', e.target.value)}
                  placeholder={t.optionalFieldPlaceholder}
                />
              </div>
            </div>
          </div>

          {/* Advertisement Section */}
          <div className="calculator-ad-section">
            <AdSenseCard
              adClient={ADSENSE_CONFIG.CLIENT_ID}
              adSlot={ADSENSE_CONFIG.AD_SLOT}
              showPlaceholder={ADSENSE_CONFIG.SHOW_PLACEHOLDER}
              index={100}
            />
          </div>

          {/* Additional Discounts & Charges - Optional Fields */}
          <div className="form-section charges-section">
            <h3>{t.additionalDiscountsCharges} (Optional)</h3>
            <div className="input-fields-container">
              <div className="input-group input-group-optional">
                <label className="label-with-hover-tooltip" onMouseEnter={() => setTooltipVisible('monthlyPlanDiscount')} onMouseLeave={() => setTooltipVisible('')}>
                  {t.monthlyPlanDiscount}
                  {tooltipVisible === 'monthlyPlanDiscount' && <div className="tooltip-content-hover">{t.monthlyPlanDiscountTooltip}</div>}
                </label>
                <input type="number" step="0.01" value={formData.monthlyPlanDiscount} onChange={(e) => handleInputChange('monthlyPlanDiscount', e.target.value)} placeholder={t.optionalFieldPlaceholder} />
              </div>

              <div className="input-group input-group-optional">
                <label className="label-with-hover-tooltip" onMouseEnter={() => setTooltipVisible('bringItBack')} onMouseLeave={() => setTooltipVisible('')}>
                  {t.bringItBackPremium}
                  {tooltipVisible === 'bringItBack' && <div className="tooltip-content-hover">{t.bringItBackPremiumTooltip}</div>}
                </label>
                <input type="number" step="0.01" value={formData.bringItBack} onChange={(e) => handleInputChange('bringItBack', e.target.value)} placeholder={t.optionalFieldPlaceholder} />
              </div>

              <div className="input-group input-group-optional">
                <label className="label-with-hover-tooltip" onMouseEnter={() => setTooltipVisible('giftCard')} onMouseLeave={() => setTooltipVisible('')}>
                  {t.giftCardPromoValue}
                  {tooltipVisible === 'giftCard' && <div className="tooltip-content-hover">{t.giftCardPromoValueTooltip}</div>}
                </label>
                <input type="number" step="0.01" value={formData.giftCard} onChange={(e) => handleInputChange('giftCard', e.target.value)} placeholder={t.optionalFieldPlaceholder} />
              </div>

              <div className="input-group input-group-optional">
                <label className="label-with-hover-tooltip" onMouseEnter={() => setTooltipVisible('additionalDiscount')} onMouseLeave={() => setTooltipVisible('')}>
                  {t.additionalDiscount}
                  {tooltipVisible === 'additionalDiscount' && <div className="tooltip-content-hover">{t.additionalDiscountTooltip}</div>}
                </label>
                <input type="number" step="0.01" value={formData.additionalDiscount} onChange={(e) => handleInputChange('additionalDiscount', e.target.value)} placeholder={t.optionalFieldPlaceholder} />
              </div>
            </div>
          </div>

          <button className="calculate-button" onClick={calculateDeal}>
            <Calculator size={20} />
            {t.calculateDeal}
          </button>

          {/* Fields Legend */}
          <div className="fields-legend">
            <p>{t.fieldsLegend}</p>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className={`results-section ${result.dealQuality ? `${result.dealQuality}-deal` : ''}`}>
            <div className="results-header">
              <DollarSign size={24} />
              <h2>{t.costAnalysis}</h2>
            </div>

            <div className="results-content">
              <div className="result-summary">
                {/* First paragraph: Real device monthly cost */}
                <p>
                  {t.whileDeviceFinancingIsListedAt} <strong>{formatCurrency(result.phoneMonthlyPrice)}{t.monthSuffix}</strong>{t.consideringThatTheDeviceEligiblePlanCosts} <strong>{formatCurrency(Math.abs(result.planDifferenceMonthly))}{t.monthSuffix} {result.planDifferenceMonthly > 0 ? t.more : t.less}</strong> {t.thanAComparableBYODPlan}
                  {result.additionalDiscount > 0 && (
                    <>{t.butThatYouAlsoHaveA} <strong>{formatCurrency(result.additionalDiscount)}</strong> {t.additionalDiscountText} {t.thatEffectivelyReducesTheCostBy} <strong>{formatCurrency(result.additionalDiscountMonthly)}{t.monthSuffix}</strong></>
                  )}{t.theDeviceMonthlyCostIs} <strong>{formatCurrency(result.phoneMonthlyPrice - result.additionalDiscountMonthly)}{t.monthSuffix}</strong>{t.butItsAkinToPaying} <strong>{formatCurrency(result.realDeviceMonthlyPrice)}{t.monthSuffix}</strong>.
                </p>

                {/* Second paragraph: Total device cost over 24 months */}
                <p>
                  {t.over24Months}, {t.consideringInitialDevicePrice} <strong>{formatCurrency(result.phoneMonthlyPrice)}{t.monthSuffix}</strong> {t.devicePrice2}
                  {result.additionalDiscount > 0 && (
                    <>, {t.yourText} <strong>{formatCurrency(result.additionalDiscount)}</strong> {t.additionalDiscountText}</>
                  )}
                  {result.giftCard > 0 && (
                    <>, {t.yourText} <strong>{formatCurrency(result.giftCardPreTaxValue)} ({formatCurrency(result.giftCard)})</strong> {t.giftCardPromotionText}</>
                  )}
                  {result.bringItBack > 0 && (
                    <> {t.andThe} <strong>{formatCurrency(result.bringItBack)}</strong> {t.bringItBackPremiumText}</>
                  )}
                  {result.planDifferenceTotal > 0 && (
                    <>{t.plusThe} <strong>{formatCurrency(result.planDifferenceTotal)}</strong> {t.inText} {t.additionalPlanCosts}</>
                  )}
                  {t.yourRealDeviceCostIs} <strong>{formatCurrency(result.effectivePhoneCost)}</strong>.
                </p>

                {/* Third paragraph: Monthly plan discount (if applicable) */}
                {result.monthlyPlanDiscount > 0 && (
                  <p>
                    {t.withYourText} <strong>{formatCurrency(result.monthlyPlanDiscount)}{t.monthSuffix}</strong> {t.planDiscountApplied},
                    {' '}{t.yourEffectivePlanCostIs} <strong>{formatCurrency(result.adjustedDevicePlanPrice)}{t.monthSuffix}</strong>,
                    {' '}{t.savingYou} <strong>{formatCurrency(result.monthlyPlanDiscount * result.contractLength)}</strong> {t.over24MonthsOnYourPlan}.
                  </p>
                )}

                {/* Fourth paragraph: Total commitment */}
                <p>
                  {t.yourTotalCommitmentOver2Years} <strong>{formatCurrency(result.totalPlanCost)}</strong> {t.forThePlan}
                  {' '}{t.andText} <strong>{formatCurrency(result.effectiveDeviceFinancing)}</strong> {t.forDeviceFinancing},
                  {' '}{t.totaling} <strong>{formatCurrency(result.totalCost)}</strong>.
                </p>

                {/* Current plan comparison (if provided) */}
                {result.totalMonthlyComparison !== null && (
                  <p>
                    {t.youllBePaying} <strong>{formatCurrency(result.totalMonthlyCost)}</strong> {t.perMonthWithThisNewPlanAndDevice} <strong>{formatCurrency(result.currentPlanAfterTax)}{t.monthSuffix}</strong>{t.thatsAnEstimated} {result.totalMonthlyComparison > 0 ? t.savingOf : t.additionalCostOf} {t.ofText} <strong>{formatCurrency(Math.abs(result.totalMonthlyComparison))}{t.monthSuffix}</strong>.
                  </p>
                )}

                {/* Fifth paragraph: MSRP comparison (if provided) */}
                {result.hasMSRP && (
                  <p>
                    {t.comparedToTheDeviceMSRPOf} <strong>{formatCurrency(result.msrp)}</strong>{t.withARealDeviceCostOf} <strong>{formatCurrency(result.effectivePhoneCost)}</strong> {t.includingAllPromotions}, {t.youre} {result.theoreticalSavings > 0 ? t.savingText : t.payingText} <strong>
                      {formatCurrency(Math.abs(result.theoreticalSavings))}
                    </strong> {t.onTheDeviceText}.
                  </p>
                )}
              </div>

              {result.hasMSRP && (
                <div className={`verdict ${result.dealQuality}`}>
                  {result.dealQuality === 'excellent' && (
                    <>
                      <TrendingUp size={24} />
                      <h3>{t.excellentDeal}</h3>
                      <p>{t.excellentDealSubtext.replace('{percent}', result.savingsPercentage)}</p>
                    </>
                  )}
                  {result.dealQuality === 'good' && (
                    <>
                      <TrendingUp size={24} />
                      <h3>{t.goodDeal}</h3>
                      <p>{t.goodDealSubtext.replace('{percent}', result.savingsPercentage)}</p>
                    </>
                  )}
                  {result.dealQuality === 'fair' && (
                    <>
                      <AlertCircle size={24} />
                      <h3>{t.fairDeal}</h3>
                      <p>{t.fairDealSubtext.replace('{percent}', result.savingsPercentage)}</p>
                    </>
                  )}
                  {result.dealQuality === 'bad' && (
                    <>
                      <TrendingDown size={24} />
                      <h3>{t.badDeal}</h3>
                      <p>{t.badDealSubtext}</p>
                    </>
                  )}
                </div>
              )}

              <div className="breakdown-table">
                <h4>{t.costBreakdown}</h4>
                <table>
                  <tbody>
                    <tr>
                      <td>{t.deviceFinancing}</td>
                      <td>{formatCurrency(result.phoneUpfrontCost)}</td>
                    </tr>
                    {result.planDifferenceTotal !== 0 && (
                      <tr>
                        <td>{result.planDifferenceTotal > 0 ? t.extraPlanCost : t.reducedPlanCost}</td>
                        <td>{result.planDifferenceTotal > 0 ? '+' : ''}{formatCurrency(result.planDifferenceTotal)}</td>
                      </tr>
                    )}
                    {result.giftCard > 0 && (
                      <tr>
                        <td>{t.giftCardPromo}</td>
                        <td>-{formatCurrency(result.giftCardPreTaxValue)} ({formatCurrency(result.giftCard)})</td>
                      </tr>
                    )}
                    {result.bringItBack > 0 && (
                      <tr>
                        <td>{t.bringItBackPremiumLabel}</td>
                        <td>+{formatCurrency(result.bringItBack)}</td>
                      </tr>
                    )}
                    {result.additionalDiscount > 0 && (
                      <tr>
                        <td>{t.additionalDiscountLabel}</td>
                        <td>-{formatCurrency(result.additionalDiscount)}</td>
                      </tr>
                    )}
                    {result.monthlyPlanDiscount > 0 && (
                      <tr>
                        <td>{t.monthlyPlanDiscountLabel}</td>
                        <td>-{formatCurrency(result.monthlyPlanDiscount * result.contractLength)}</td>
                      </tr>
                    )}
                    <tr className="total-row">
                      <td><strong>{t.effectiveDeviceCostLabel}</strong></td>
                      <td><strong>{formatCurrency(result.effectivePhoneCost)}</strong></td>
                    </tr>
                    {result.hasMSRP && (
                      <>
                        <tr className="spacer-row">
                          <td colSpan="2"></td>
                        </tr>
                        <tr>
                          <td>{t.deviceMSRP}</td>
                          <td>{formatCurrency(result.msrp)}</td>
                        </tr>
                        <tr className={result.theoreticalSavings > 0 ? 'savings-row' : 'loss-row'}>
                          <td><strong>{result.theoreticalSavings > 0 ? t.totalSavings : t.totalLoss}</strong></td>
                          <td>
                            <strong>
                              {result.theoreticalSavings > 0 ? '' : '-'}
                              {formatCurrency(Math.abs(result.theoreticalSavings))}
                              {result.theoreticalSavings > 0 && ` (${result.savingsPercentage}%)`}
                            </strong>
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Advertisement Section - After Results */}
            <div className="calculator-ad-section">
              <AdSenseCard
                adClient={ADSENSE_CONFIG.CLIENT_ID}
                adSlot={ADSENSE_CONFIG.AD_SLOT}
                showPlaceholder={ADSENSE_CONFIG.SHOW_PLACEHOLDER}
                index={101}
              />
            </div>
          </div>
        )}

        {/* Province Selection */}
        <div className="province-section">
          <div className="province-header">
            <MapPin size={18} />
            <span>{t.provinceTerritory}</span>
          </div>
          <select
            value={selectedProvince}
            onChange={(e) => setSelectedProvince(e.target.value)}
            disabled={detectingLocation}
            className="province-select"
          >
            {detectingLocation ? (
              <option>{t.detectingLocation}</option>
            ) : (
              <>
                {Object.entries(PROVINCE_NAMES).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name} ({code}) - Tax: {(TAX_RATES[code] * 100).toFixed(2)}%
                  </option>
                ))}
              </>
            )}
          </select>
        </div>
        {/* Info Section */}
        <div className="info-section">
          <AlertCircle size={18} />
          <div>
            <p>
              <strong>{t.calculatorNote}:</strong> {renderBoldText(t.calculatorNoteFirstLine.replace('{taxRate}', selectedProvince && `${(TAX_RATES[selectedProvince] * 100).toFixed(2)}`))}
            </p>
            <p>
              {renderBoldText(t.calculatorNoteSecondLine)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculatorPage;
